import { useState } from "react";
import { AuthLayout, AuthFooterLink } from "@/features/auth/components/AuthLayout";
import { CheckEmail } from "@/features/auth/components/CheckEmail";
import { FormField } from "@/components/FormField";
import { SubmitButton } from "@/components/SubmitButton";
import { StatusBanner } from "@/components/StatusBanner";
import { signUpWithEmail } from "@/features/auth/authApi";
import { validateSignup, PASSWORD_MIN } from "@/features/auth/validation";
import { friendlyAuthError } from "@/lib/errors";
import { ROUTES } from "@/constants/routes";

const PasswordHint = ({ length }) => (
  <span data-testid="signup-password-hint" className={length >= PASSWORD_MIN ? "text-emerald-800" : ""}>
    {length >= PASSWORD_MIN ? "Length looks good." : `At least ${PASSWORD_MIN} characters · ${length}/${PASSWORD_MIN}`}
  </span>
);

export default function SignupPage() {
  const [values, setValues] = useState({ fullName: "", email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const onChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validateSignup(values);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) return;
    setSubmitting(true);
    const { data, error } = await signUpWithEmail(values).catch((err) => ({ error: err }));
    setSubmitting(false);
    if (error) return setFormError(friendlyAuthError(error));
    // With a session, PublicOnlyRoute redirects and profile init runs; otherwise confirmation is required.
    if (!data?.session) setSentTo(values.email.trim());
  };

  if (sentTo) return <CheckEmail email={sentTo} onReset={() => setSentTo(null)} />;

  return (
    <AuthLayout
      eyebrow="Create account"
      title="Create your MetWorth."
      subtitle="One professional identity, set up in a minute."
      footer={<AuthFooterLink text="Already have an account?" to={ROUTES.login} label="Sign in" testId="signup-to-login-link" />}
    >
      <form data-testid="signup-form" onSubmit={onSubmit} noValidate className="space-y-6">
        {formError && <StatusBanner testId="signup-error-banner">{formError}</StatusBanner>}
        <FormField id="signup-fullname" name="fullName" label="Full name" autoComplete="name" testId="signup-fullname-input" value={values.fullName} onChange={onChange} error={errors.fullName} />
        <FormField id="signup-email" name="email" type="email" label="Email" autoComplete="email" testId="signup-email-input" value={values.email} onChange={onChange} error={errors.email} />
        <FormField id="signup-password" name="password" type="password" label="Password" autoComplete="new-password" testId="signup-password-input" value={values.password} onChange={onChange} error={errors.password} hint={<PasswordHint length={values.password.length} />} />
        <SubmitButton loading={submitting} loadingLabel="Creating account" testId="signup-submit-button">
          Create account
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
